import { INVOICE_APIS } from "@/api_framework/api_config";
import { useConfiguration } from "@/app/hooks/useConfiguration";
import { useCurlLogger } from "@/app/hooks/useCurlLogger";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import { setLoading } from "@/app_redux/reducers/loading_reducer";
import axios, { AxiosError, AxiosResponse } from "axios";
import AxiosCurlirize from "axios-curlirize";
import { useCallback } from "react";
import { useAPIUtils } from "../../useAPIUtils";

AxiosCurlirize(axios, () => {});

export interface IDailyRevenuePaymentsParams {
  clinic_id: string;
  start_date: number;
  end_date: number;
  page_number: number;
  page_size: number;
}

export interface IDailyRevenuePaymentsResult {
  user_id: string;
  user_name: string;
  pet_id?: string | null;
  invoiced_value: number;
  payment_done: number;
  wallet_transactions: number;
  delta: number;
  overdue: number;
  has_invoice: boolean;
  is_booking_present: boolean;
}

export interface IDailyRevenuePaymentsData {
  total_revenue: number;
  total_payment: number;
  total_wallet_transactions: number;
  total_delta: number;
  total_overdue: number;
  total_records: number;
  page_number: number;
  page_size: number;
  results: IDailyRevenuePaymentsResult[];
}

interface IAPIResponse {
  status: boolean;
  message: string;
  data: IDailyRevenuePaymentsData;
}

export const useDailyRevenuePaymentsAPI = () => {
  const dispatch = useAppDispatch();
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const execute = useCallback(
    async (params: IDailyRevenuePaymentsParams): Promise<{ status: boolean; message: IDailyRevenuePaymentsData | null }> => {
      try {
        dispatch(
          setLoading({
            key: "isFinanceSummaryLoading",
            status: true,
          }),
        );

        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);

        const response = await axios
          .post(INVOICE_APIS?.DAILY_REVENUE_PAYMENTS?.baseURL ?? "", params, {
            headers: {
              Authorization: `${accessToken}`,
              "Content-Type": "application/json",
            },
          })
          .then((res: AxiosResponse<IAPIResponse>) => {
            dispatch(
              setLoading({
                key: "isFinanceSummaryLoading",
                status: false,
              }),
            );

            if (res.data.status) {
              return { status: true, message: res.data.data };
            }

            return { status: false, message: null };
          })
          .catch((e: AxiosError) => {
            dispatch(
              setLoading({
                key: "isFinanceSummaryLoading",
                status: false,
              }),
            );
            sendCurl(e.config?.["curlCommand"]);
            captureError(e);
            return { status: false, message: null };
          });

        return response;
      } catch (e) {
        dispatch(
          setLoading({
            key: "isFinanceSummaryLoading",
            status: false,
          }),
        );
        captureError(e);
        return { status: false, message: null };
      }
    },
    [captureError, configuration.localStorage.ACCESS_TOKEN.key, dispatch, sendCurl],
  );

  return { execute };
};
